import React, { useEffect, useRef, useState } from 'react';
import { formatMoney } from '../utils/format';

// Категориальные цвета в фиксированном порядке (проверены на различимость при дальтонизме)
export const SERIES_COLORS = ['#2a78d6', '#eb6834'];

export interface BarSeries { key: string; name: string; color: string }
export interface BarDatum { label: string; values: Record<string, number> }

const compact = (n: number) => Math.abs(n) >= 1e6 ? `${(n / 1e6).toFixed(1)} млн` : Math.abs(n) >= 1e3 ? `${Math.round(n / 1e3)} тыс` : String(Math.round(n));

// Сгруппированные столбцы по периодам: тонкие столбцы, скругление 4px у данных, 2px зазор,
// подсказка при наведении на группу, легенда для 2+ рядов и табличный вид
export const BarChart: React.FC<{ data: BarDatum[]; series: BarSeries[]; height?: number; title?: string }> =
    ({ data, series, height = 180, title }) => {
    const [hover, setHover] = useState<number | null>(null);
    const [asTable, setAsTable] = useState(false);
    // Рисуем в реальных пикселях контейнера: при масштабировании viewBox текст раздувался бы на широком экране
    const boxRef = useRef<HTMLDivElement>(null);
    const [W, setW] = useState(640);
    useEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const ro = new ResizeObserver(entries => setW(Math.max(280, Math.round(entries[0].contentRect.width))));
        ro.observe(el);
        return () => ro.disconnect();
    }, [asTable, data.length]);
    if (data.length === 0) return <div className="text-xs text-slate-400 py-4 text-center">Нет данных за период</div>;

    const max = Math.max(1, ...data.flatMap(d => series.map(s => d.values[s.key] || 0)));
    const H = height, top = 12, bottom = 22, left = 56;
    const plotH = H - top - bottom, groupW = (W - left) / data.length;
    const barW = Math.max(4, Math.min(18, (groupW - 12) / series.length - 2));
    const y = (v: number) => top + plotH - (Math.max(0, v) / max) * plotH;
    const ticks = [0, 0.5, 1].map(f => f * max);

    return (
        <div className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-bold text-slate-600">{title}</span>
                <span className="flex items-center gap-3">
                    {series.length > 1 && series.map(s => (
                        <span key={s.key} className="flex items-center gap-1 text-slate-600"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: s.color }} />{s.name}</span>
                    ))}
                    <button type="button" onClick={() => setAsTable(v => !v)} className="text-blue-600 hover:underline">{asTable ? 'График' : 'Таблица'}</button>
                </span>
            </div>
            {asTable ? (
                <table className="w-full text-xs">
                    <thead><tr className="text-slate-500"><th className="text-left py-1">Период</th>{series.map(s => <th key={s.key} className="text-right py-1">{s.name}</th>)}</tr></thead>
                    <tbody>{data.map(d => (
                        <tr key={d.label} className="border-t border-slate-100"><td className="py-1">{d.label}</td>
                            {series.map(s => <td key={s.key} className="text-right font-mono py-1">{formatMoney(d.values[s.key] || 0)}</td>)}</tr>
                    ))}</tbody>
                </table>
            ) : (
                <div className="relative" ref={boxRef}>
                    <svg width={W} height={H} role="img" aria-label={title}>
                        {ticks.map(t => (
                            <g key={t}>
                                <line x1={left} x2={W} y1={y(t)} y2={y(t)} stroke="#e2e8f0" strokeWidth={1} />
                                <text x={left - 6} y={y(t) + 3} textAnchor="end" fontSize={10} fill="#64748b">{compact(t)}</text>
                            </g>
                        ))}
                        {data.map((d, i) => {
                            const x0 = left + i * groupW + (groupW - series.length * (barW + 2)) / 2;
                            return (
                                <g key={d.label} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}>
                                    {/* зона наведения шире столбцов */}
                                    <rect x={left + i * groupW} y={top} width={groupW} height={plotH} fill={hover === i ? '#f1f5f9' : 'transparent'} />
                                    {series.map((s, j) => {
                                        const v = d.values[s.key] || 0, h = Math.max(0, top + plotH - y(v));
                                        const x = x0 + j * (barW + 2), r = Math.min(4, barW / 2, h);
                                        return h > 0 ? (
                                            <path key={s.key} fill={s.color}
                                                  d={`M${x},${top + plotH} V${y(v) + r} Q${x},${y(v)} ${x + r},${y(v)} H${x + barW - r} Q${x + barW},${y(v)} ${x + barW},${y(v) + r} V${top + plotH} Z`} />
                                        ) : null;
                                    })}
                                    <text x={left + i * groupW + groupW / 2} y={H - 6} textAnchor="middle" fontSize={10} fill="#64748b">{d.label}</text>
                                </g>
                            );
                        })}
                        <line x1={left} x2={W} y1={top + plotH} y2={top + plotH} stroke="#94a3b8" strokeWidth={1} />
                    </svg>
                    {hover !== null && (
                        <div className="absolute top-0 pointer-events-none bg-white border border-slate-200 shadow-md rounded px-2 py-1 text-xs"
                             style={{ left: `${Math.min(80, ((hover + 0.5) / data.length) * 100)}%` }}>
                            <div className="font-bold text-slate-700">{data[hover].label}</div>
                            {series.map(s => (
                                <div key={s.key} className="flex items-center gap-1 text-slate-600">
                                    <span className="inline-block w-2 h-2 rounded-sm" style={{ background: s.color }} />{s.name}: <span className="font-mono text-slate-800">{formatMoney(data[hover].values[s.key] || 0)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
